import { useMachine } from "@xstate/react";
import { ReactElement, useEffect, useMemo } from "react";

import drumroll from "../../assets/audio/drumroll.mp3";
import looseAudio from "../../assets/audio/loose.mp3";
import openDoorAudio from "../../assets/audio/Prijzenkast_deurtje_open_korter.mp3";
import cursorAuto from "../../assets/cursors/cursor_auto.png";
import cursorPointer from "../../assets/cursors/cursor_pointer.png";
import { ReactComponent as Speaker } from "../../assets/icons/volume-high.svg";
import confettiImage from "../../assets/img/confetti.gif";
import trophyImage from "../../assets/img/trophy.webp";
import deurtjeLinks from "../../assets/links.webp";
import deurtjeMidden from "../../assets/midden.webp";
import deurtjeRechts from "../../assets/rechts.webp";
import { GameContainerProps } from "../Game/Game.container";
import IntroLastFrame from "../IntroLastFrame";
import MaybeAudio from "../MaybeAudio";
import ReactionVideo from "../ReactionVideo";

import {
  AbsoluteImagesContainer,
  Button,
  ButtonBottomRight,
  Buttons,
  Confetti,
  Container,
  Door,
  Image,
  Images,
  ImageWrapper,
  NeonAnimation,
  Trophy,
} from "./components";
import { gameMachine } from "./gameMachine";

const deurtjes = [deurtjeLinks, deurtjeMidden, deurtjeRechts];

type Word = {
  readonly imageUri: string;
  readonly isCorrect?: boolean;
  readonly altText?: string;
  readonly audioUri?: string;
};

type Words = readonly Word[];

export type Props = {
  readonly word: string;
  readonly words: Words;
  readonly introAudioUri?: string;
  readonly successAudioUri?: string;
  readonly onWinRound?: () => void;
  readonly isMuted?: boolean;
} & Pick<GameContainerProps, "bgMusicRef">;

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const GameRound = ({
  word,
  words,
  successAudioUri,
  introAudioUri,
  onWinRound,
  isMuted,
  bgMusicRef,
}: Props): ReactElement => {
  const [state, send] = useMachine<typeof gameMachine>(gameMachine);

  const { selectedWord, words: shuffledWords, wordAudioIndex } = state.context;

  useEffect(() => {
    send({
      type: "ON_GAME_START",
      data: {
        word,
        words,
        successAudioUri,
        introAudioUri,
      },
    });
  }, [introAudioUri, send, successAudioUri, word, words]);

  useEffect(() => {
    if (state.matches("HasGuessedWrong.Initial")) {
      void new Audio(looseAudio).play();
    }
  }, [state]);

  useEffect(() => {
    if (state.matches("HasWon")) onWinRound && onWinRound();
  }, [onWinRound, state]);

  const isPlayingAudio = useMemo(
    () =>
      state.matches("IsIntroAudioPlaying") ||
      state.matches(
        "HasGuessedCorrect.GuessCorrectMedia.IsReactionVideoPlaying"
      ) ||
      state.matches("HasGuessedWrong.IsReactionVideoPlaying") ||
      state.matches(
        "HasGuessedCorrect.GuessCorrectMedia.IsWinAudioPlaying.Play"
      ) ||
      state.matches("IsShuffling") ||
      state.matches("HasGuessedWrong.Initial") ||
      state.matches("IsGuessing.IsWordAudioPlaying"),
    [state]
  );

  useEffect(() => {
    if (!bgMusicRef?.current) {
      return;
    }
    if (isMuted) {
      bgMusicRef.current.volume = 0;
      return;
    }

    bgMusicRef.current.volume = isPlayingAudio ? 0.15 : 0.35;
  }, [bgMusicRef, isMuted, isPlayingAudio]);

  const onWordAudioEnd = () => send("ON_WORD_AUDIO_END");
  const onIntroAudioEnd = () => send("ON_INTRO_AUDIO_END");
  const onSuccessAudioEnd = () => send("ON_WIN_AUDIO_END");
  const onGuessWord = (word: Word) => () => send("ON_GUESS", { word });
  const onReactionVideoEnd = () => send("ON_REACTION_VIDEO_END");
  const onReplayIntroAudio = () => send("ON_REPLAY_INTRO_AUDIO");

  return (
    <>
      <Container>
        <IntroLastFrame />
        <AbsoluteImagesContainer>
          <Images>
            {shuffledWords.map(({ imageUri, altText, audioUri }, index) => (
              <ImageWrapper
                key={index}
                shouldFadeOut={
                  state.matches("HasGuessedWrong.IsFading") ||
                  state.matches("HasGuessedCorrect.IsFading") ||
                  state.matches("HasWon")
                }
              >
                <Image src={imageUri} alt={altText} />
                {state.matches("IsGuessing.IsWordAudioPlaying") &&
                  wordAudioIndex === index && (
                    <MaybeAudio src={audioUri} onEnded={onWordAudioEnd} />
                  )}
              </ImageWrapper>
            ))}
          </Images>
        </AbsoluteImagesContainer>
        <AbsoluteImagesContainer>
          <Buttons>
            {shuffledWords.map((word, index) => {
              const isSelected: boolean =
                !!selectedWord && word.imageUri === selectedWord?.imageUri;
              const isCorrectWord: boolean =
                isSelected && state.matches("HasGuessedCorrect");

              return (
                <Button
                  style={{
                    cursor: state.matches("IsGuessing")
                      ? `url(${cursorPointer}) 28 0, auto`
                      : `url(${cursorAuto}), auto`,
                  }}
                  onClick={
                    state.matches("IsGuessing") ? onGuessWord(word) : undefined
                  }
                  key={index}
                >
                  <NeonAnimation />
                  <Door
                    src={deurtjes[index]}
                    alt="open deurtje"
                    isVisible={isSelected}
                    index={index}
                  />
                  {isSelected && <audio src={openDoorAudio} autoPlay />}
                  <Trophy
                    index={index}
                    src={trophyImage}
                    isVisible={isCorrectWord}
                    isAnimating={state.matches(
                      "HasGuessedCorrect.GuessCorrectMedia.IsWinAudioPlaying"
                    )} // Stop trophy animation after playing audio to improve performance while playing video
                  />
                  <Confetti src={confettiImage} isVisible={isCorrectWord} />
                </Button>
              );
            })}
          </Buttons>

          {state.matches("IsShuffling") && <audio src={drumroll} autoPlay />}
          {state.matches("IsIntroAudioPlaying") && (
            <MaybeAudio src={introAudioUri} onEnded={onIntroAudioEnd} />
          )}
          {state.matches(
            "HasGuessedCorrect.GuessCorrectMedia.IsWinAudioPlaying.Play"
          ) && <MaybeAudio src={successAudioUri} onEnded={onSuccessAudioEnd} />}
          <ReactionVideo
            isCorrect={state.matches("HasGuessedCorrect")}
            shouldPlayVideo={
              state.matches(
                "HasGuessedCorrect.GuessCorrectMedia.IsReactionVideoPlaying"
              ) || state.matches("HasGuessedWrong.IsReactionVideoPlaying")
            }
            onEnded={onReactionVideoEnd}
          />
        </AbsoluteImagesContainer>
      </Container>
      {state.matches("IsGuessing") && (
        <ButtonBottomRight onClick={onReplayIntroAudio} Icon={Speaker}>
          Nog eens
        </ButtonBottomRight>
      )}
    </>
  );
};
export default GameRound;
